import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_2.mp4";

const RARITY_ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const RARITY_COLORS = {
  common: 'var(--tier-common)',
  uncommon: 'var(--tier-uncommon)',
  rare: 'var(--tier-rare)',
  epic: 'var(--tier-epic)',
  legendary: 'var(--tier-legendary)',
};

const SLOT_SHORT = { lmb: 'LMB', rmb: 'RMB', f: 'F', ult: 'Q', passive: 'Passive' };

function RootCard({ root }) {
  const isStub = root.status === 'stub';
  const actives = (root.abilities || []).filter(a => a.slot !== 'passive');
  const passives = (root.abilities || []).filter(a => a.slot === 'passive');

  return (
    <Link to={`/wiki/root/${root.slug}`} style={{ textDecoration: 'none' }}>
      <div
        className="wiki-detail-box"
        style={{
          '--root-c': root.color,
          position: 'relative', overflow: 'hidden',
          padding: '1.1rem 1.2rem',
          display: 'flex', flexDirection: 'column', gap: '0.55rem',
          height: '100%', boxSizing: 'border-box',
          transition: 'border-color 0.18s, transform 0.18s, box-shadow 0.18s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = root.color;
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = `0 8px 26px ${root.color}22`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = '';
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '';
        }}
      >
        {/* top accent bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, ${root.color}, transparent)`,
        }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `radial-gradient(circle, ${root.color}2e 0%, transparent 70%)`,
          }}>
            <McIcon materialKey={root.material} size={36} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: root.color, lineHeight: 1.2 }}>
              {root.name}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
              {root.playstyle || 'Root'}
            </div>
          </div>
          {isStub && (
            <span style={{
              marginLeft: 'auto', flexShrink: 0,
              fontSize: '0.58rem', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase',
              color: 'var(--text-muted)', border: '1px dashed var(--border-mid)',
              borderRadius: '2rem', padding: '3px 9px',
            }}>Soon</span>
          )}
        </div>

        <p style={{
          margin: 0, fontSize: '0.8rem', lineHeight: 1.55, color: 'var(--text-secondary)',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {root.description}
        </p>

        <div style={{
          marginTop: 'auto', paddingTop: '0.55rem', borderTop: '1px solid var(--border)',
          display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center',
        }}>
          {actives.map(a => (
            <span key={a.id} style={{
              fontSize: '0.65rem', fontWeight: 700, color: `${root.color}dd`,
              background: `${root.color}12`, border: `1px solid ${root.color}33`,
              borderRadius: '0.35rem', padding: '2px 7px', whiteSpace: 'nowrap',
            }}>
              <span style={{ opacity: 0.6, marginRight: 4 }}>{SLOT_SHORT[a.slot]}</span>{a.name}
            </span>
          ))}
          {passives.length > 0 && (
            <span style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-muted)' }}>
              +{passives.length} passive{passives.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function WikiRootsList({ roots }) {
  const visible = (roots || []).filter(r => Number(r.enabled) !== 0);

  const byRarity = RARITY_ORDER
    .map(rarity => ({ rarity, roots: visible.filter(r => r.rarity === rarity) }))
    .filter(g => g.roots.length > 0);

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <video className="wiki-detail-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': '#8a4dff' }}>
              <McIcon materialKey="nether_star" size={56} />
            </div>
            <div>
              <h1 className="wiki-detail-item-name" style={{
                background: 'linear-gradient(90deg, #e8642c 0%, #f5d848 30%, #52c078 55%, #7fd4ff 78%, #8a4dff 100%)',
                WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                The Roots
              </h1>
              <div className="wiki-detail-item-type" style={{ color: 'var(--text-secondary)' }}>
                {visible.length} elemental powers · pick one, master it
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <div className="container" style={{ paddingBottom: '4rem' }}>
        <p style={{
          maxWidth: 720, color: 'var(--text-secondary)', fontSize: '0.9rem',
          lineHeight: 1.7, margin: '0 0 2rem',
        }}>
          Every player equips a <strong style={{ color: 'var(--text-primary)' }}>Root</strong> — an elemental
          power with its own abilities and playstyle. Each root has a left-click and right-click ability,
          an ultimate on Q (some even get a fourth on F), plus passives. Roots scale across three tiers:
          higher tiers mean more damage, longer durations and bigger areas.
        </p>

        {byRarity.map(group => (
          <div key={group.rarity} style={{ marginBottom: '2.25rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.9rem',
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: RARITY_COLORS[group.rarity],
              }} />
              <h2 style={{
                margin: 0, fontSize: '0.85rem', fontWeight: 800, letterSpacing: '2.5px',
                textTransform: 'uppercase', color: RARITY_COLORS[group.rarity],
              }}>
                {group.rarity}
              </h2>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                {group.roots.length} root{group.roots.length > 1 ? 's' : ''}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>

            <StaggerContainer style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
              gap: '0.9rem',
            }}>
              {group.roots.map(root => (
                <StaggerItem key={root.slug} style={{ display: 'flex', flexDirection: 'column' }}>
                  <RootCard root={root} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        ))}
      </div>
    </div>
  );
}
